import { useTranslation } from '@kgc/i18n';

import { STATUS_LABEL_KEYS, WorksheetStatus } from '../types';

/**
 * Linear happy-path lifecycle nodes shown as the stepper.
 * Mirrors the real worksheet state-machine (NOT the raw enum declaration order),
 * per Zeno's design ratify (2026-06-07).
 */
export const WORKSHEET_STATE_FLOW: WorksheetStatus[] = [
  WorksheetStatus.FELVEVE,
  WorksheetStatus.FOLYAMATBAN,
  WorksheetStatus.KESZ,
  WorksheetStatus.SZAMLAZANDO,
  WorksheetStatus.LEZART,
];

/**
 * Off-flow "paused" states: the work is halted (waiting for parts / suspended).
 * They overlay onto the work node rather than being a separate linear step.
 */
export const WORKSHEET_PAUSED_STATES: WorksheetStatus[] = [
  WorksheetStatus.VARHATO,
  WorksheetStatus.FELFUGGESZTVE,
];

/** The linear node a paused state maps onto (work-in-progress). */
const PAUSED_NODE: WorksheetStatus = WorksheetStatus.FOLYAMATBAN;

type StepState = 'completed' | 'current' | 'upcoming';

interface WorksheetStateStepperProps {
  status: WorksheetStatus | string;
  className?: string;
}

function CheckIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={3}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

/**
 * Horizontal lifecycle stepper for the worksheet detail page.
 * Additive, status-derived (no behaviour change) — safe to render unconditionally.
 */
export function WorksheetStateStepper({ status, className }: WorksheetStateStepperProps) {
  const { t } = useTranslation();

  const isCancelled = status === WorksheetStatus.TOROLVE;
  const isPaused = WORKSHEET_PAUSED_STATES.includes(status as WorksheetStatus);
  const effectiveStatus = isPaused ? PAUSED_NODE : (status as WorksheetStatus);
  const currentIdx = isCancelled ? -1 : WORKSHEET_STATE_FLOW.indexOf(effectiveStatus);

  const stateFor = (idx: number): StepState => {
    if (isCancelled || currentIdx < 0) return 'upcoming';
    if (idx < currentIdx) return 'completed';
    if (idx === currentIdx) return 'current';
    return 'upcoming';
  };

  const circleClasses: Record<StepState, string> = {
    completed: 'bg-green-500 text-white border-green-500',
    current: 'bg-primary text-white border-primary',
    upcoming:
      'bg-white dark:bg-[#1a4f5c] text-gray-400 dark:text-gray-500 border-gray-300 dark:border-[#3a9aa8]',
  };
  const labelClasses: Record<StepState, string> = {
    completed: 'text-gray-700 dark:text-gray-200',
    current: 'text-primary font-semibold',
    upcoming: 'text-gray-400 dark:text-gray-500',
  };

  return (
    <nav
      aria-label={t('worksheet.stepper.ariaLabel')}
      data-testid="worksheet-state-stepper"
      className={`rounded-lg border border-gray-200 dark:border-[#3a9aa8] bg-white dark:bg-[#1a4f5c] px-4 py-3 ${className ?? ''}`}
    >
      <ol className="flex items-center gap-1 overflow-x-auto">
        {WORKSHEET_STATE_FLOW.map((s, i) => {
          const state = stateFor(i);
          const showPausedHere = isPaused && i === currentIdx;
          const isLast = i === WORKSHEET_STATE_FLOW.length - 1;
          return (
            <li
              key={s}
              data-testid={`stepper-step-${s}`}
              data-state={state}
              aria-current={state === 'current' ? 'step' : undefined}
              className="flex items-center gap-1 flex-shrink-0"
            >
              <div className="flex items-center gap-2">
                <span
                  className={`flex h-7 w-7 items-center justify-center rounded-full border text-xs font-semibold ${circleClasses[state]}`}
                >
                  {state === 'completed' ? (
                    <>
                      <CheckIcon />
                      <span className="sr-only">{t('worksheet.stepper.completed')}</span>
                    </>
                  ) : (
                    i + 1
                  )}
                </span>
                <span className={`text-sm whitespace-nowrap ${labelClasses[state]}`}>
                  {t(STATUS_LABEL_KEYS[s])}
                  {state === 'current' && (
                    <span className="sr-only"> ({t('worksheet.stepper.current')})</span>
                  )}
                </span>
                {showPausedHere && (
                  <span
                    data-testid="stepper-paused"
                    data-status={status}
                    className="ml-1 inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/50 dark:text-orange-300"
                  >
                    {t(STATUS_LABEL_KEYS[status as WorksheetStatus]) || status}
                  </span>
                )}
              </div>
              {!isLast && (
                <span
                  aria-hidden="true"
                  className={`mx-1 h-0.5 w-6 flex-shrink-0 ${
                    state === 'completed' ? 'bg-green-500' : 'bg-gray-200 dark:bg-[#3a9aa8]/40'
                  }`}
                />
              )}
            </li>
          );
        })}
      </ol>

      {isCancelled && (
        <div
          data-testid="stepper-cancelled"
          className="mt-2 inline-flex items-center gap-2 rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-800 dark:bg-red-900/50 dark:text-red-300"
        >
          {t('worksheet.stepper.cancelled')}
        </div>
      )}
    </nav>
  );
}
